/**
 * Cliente HTTP del dominio de direcciones (MVP-02B §3).
 *
 * Única puerta de entrada a la API de direcciones y geografía. Antes cada
 * pantalla llamaba a axios por su cuenta, con rutas escritas de dos formas
 * distintas (`api/provincias/` en Perfil, `/api/provincias/` en Checkout) y
 * cada una interpretando la respuesta a su manera. Aquí las rutas se escriben
 * una sola vez y siempre con barra inicial.
 */
import axios from "axios";

/** El backend normaliza igual; hacerlo también aquí evita ida y vuelta inútil. */
export const normalizarCodigoPostal = (raw) =>
    String(raw ?? "").replace(/\D/g, "").slice(0, 5);

export const DIRECCION_VACIA = {
    pais: "Spain",
    nombre_contacto: "",
    telefono: "",
    direccion: "",
    apartamento: "",
    provincia: "",
    municipio: "",
    codigo_postal: "",
    predeterminado: false,
};

// --- Direcciones del usuario -------------------------------------------------

export const fetchDirecciones = () =>
    axios.get("/api/direcciones/").then((res) => res.data ?? []);

export const fetchDireccion = (id) =>
    axios.get(`/api/direcciones/${id}/`).then((res) => res.data);

export const createDireccion = (direccion) =>
    axios.post("/api/direcciones/", direccion).then((res) => res.data);

export const updateDireccion = (id, direccion) =>
    axios.put(`/api/direcciones/${id}/`, direccion).then((res) => res.data);

export const deleteDireccion = (id) => axios.delete(`/api/direcciones/${id}/`);

// --- Geografía ---------------------------------------------------------------

export const fetchProvincias = () =>
    axios.get("/api/provincias/").then((res) => res.data ?? []);

export const fetchMunicipios = (provinciaId) =>
    axios.get(`/api/municipios/${provinciaId}/`).then((res) => res.data ?? []);

export const lookupCodigoPostal = (codigoPostal) =>
    axios.get(`/api/codigo-postal/${codigoPostal}/`).then((res) => res.data ?? []);

// --- Transformaciones --------------------------------------------------------

/**
 * Convierte una dirección guardada en el snapshot que espera el checkout.
 *
 * El backend devuelve `provincia`/`municipio` como id y los nombres en
 * `*_nombre`. Se mantiene la tolerancia a la forma antigua (nombre en el campo
 * principal) para que un bundle desplegado antes que el backend no rompa.
 */
export const direccionToCheckout = (direccion) => {
    if (!direccion) return null;

    const nombreDe = (valor, alternativo) => {
        if (typeof alternativo === "string" && alternativo) return alternativo;
        if (typeof valor === "string") return valor;
        return valor?.nombre ?? "";
    };

    return {
        contact_phone: direccion.telefono || "",
        shipping_name: direccion.nombre_contacto || "",
        shipping_address_line1: direccion.direccion || "",
        shipping_address_line2: direccion.apartamento || "",
        shipping_city: nombreDe(direccion.municipio, direccion.municipio_nombre),
        shipping_state: nombreDe(direccion.provincia, direccion.provincia_nombre),
        shipping_postal_code: direccion.codigo_postal || "",
        shipping_country: direccion.pais === "Spain" ? "España" : direccion.pais || "España",
    };
};

/** Etiqueta legible de una dirección, igual en Perfil y en Checkout. */
export const describirDireccion = (direccion) => {
    if (!direccion) return "";
    const municipio = direccion.municipio_nombre ?? direccion.municipio ?? "";
    const provincia = direccion.provincia_nombre ?? direccion.provincia ?? "";
    return [direccion.codigo_postal, municipio, provincia && `(${provincia})`]
        .filter(Boolean)
        .join(" ");
};

/**
 * Aplana los errores del serializer a `{campo: mensaje}`.
 *
 * DRF devuelve listas por campo; la UI muestra una línea por campo.
 */
export const parseErroresDireccion = (error) => {
    const data = error?.response?.data;
    if (!data || typeof data !== "object") {
        return { general: "No se pudo guardar la dirección. Inténtalo de nuevo." };
    }
    return Object.fromEntries(
        Object.entries(data).map(([campo, mensaje]) => [
            campo,
            Array.isArray(mensaje) ? mensaje.join(" ") : String(mensaje),
        ]),
    );
};
